import React from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from '../../../../../ui/toast';
import { ChevronDownIcons, FilterIcons } from '../../../../../icons/icons';
import {
  AppliedFilters,
  Button,
  DataTable,
  Drawer,
} from '../../../../../ui';
import NoDataInList from '../../../../../ui/components/Common/NoData/NoDataInList';
import { SearchIconNewUI } from '../../../../../ui/icons/icons';
import { ResultsStats } from './Components';
import {
  BehavioralSkillButton,
  TechnicalSkillButton,
} from './Containers/AssessmentResultDetails/Components/Buttons/SkillButtons';
import { FilterPreScreen } from './FilterPreScreen';
import {
  getDynamicBehaviorColumns,
  getDynamicSkillColumns,
} from './Functions/DynamicColumns';
import { getColumns } from './Functions/GetColumns';
import {
  IAssessmentResultsProps,
  useAssessmentResultList,
} from './Hooks/UseAssesmentResultList';

export const AssessmentResults: React.FC<IAssessmentResultsProps> = ({
  assessmentId,
  userResults,
  highestScore,
  completionRatio,
  candidateStatus,
  countTotal,
  countCompleted,
  updateAssessmentUserList,
  preScreenQuestions = [],
  skills = [],
  behaviorSkills = [],
  isAutoAssessment,
  autoAssessmentRoute,
  onResultClick: onResultClickProp,
}) => {
  const history = useHistory();

  const {
    calculateAverageTime,
    resultListData,
    selectedRows,
    setSelectedRows,
    transformUserResultsToApiFormat,
    exportCSV,
    onPageChange,
    handleSortChange,
    hasFetchedOnce,
    pageNumber,
    sort,
    bulkActionOpen,
    setFilters,
    filters,
    applyFilter,
    appliedFilters,
    handleClearFilters,
    handleRemoveFilter,
    selectionDropdownOpen,
    selectedItems,
    setSelectedItems,
    handleSelectAllItems,
    handleToggleSelectionDropdown,
    handleToggleBulkActionDropdown,
    handleBulkMove,
    handleSendUnsuccessfulEmails,
    onResultClick,
    toggleAllSkills,
    toggleSkillVisibility,
    showAllSkills,
    visibleSkills,
    loading,
    serverUserResults,
    serverTotalCount,
    totalPages,
    handleCloseFilter,
    filterOpen,
    handleOpenFilter,
    searchTerm,
    setSearchTerm,
  } = useAssessmentResultList({
    assessmentId,
    userResults,
    highestScore,
    completionRatio,
    candidateStatus,
    countTotal,
    countCompleted,
    updateAssessmentUserList,
    preScreenQuestions,
    skills,
    behaviorSkills,
    onResultClick: onResultClickProp, // Pass through the custom handler
  });

  return (
    <>
      <div>
        <div className='mx-auto w-full px-1.5 sm:px-2'>
          <div className='flex flex-col gap-1.5'>
            <div className='flex flex-col items-stretch justify-between gap-2 lg:flex-row lg:items-center'>
              <ResultsStats
                  countCompleted={countCompleted}
                  countTotal={countTotal}
                  calculateAverageTime={calculateAverageTime}
                  resultListData={resultListData}
              />
              <div className='flex w-full flex-col items-stretch gap-2 sm:flex-row sm:items-center lg:w-auto'>
                <div className='relative flex-1 sm:flex-none'>
                  <SearchIconNewUI className='absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500' />
                  <input
                      type='text'
                      placeholder='Search by name or email...'
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className='h-8 w-full rounded-lg border border-slate-200/60 bg-slate-50 pl-9 pr-3 text-xs font-medium text-slate-700 transition-all placeholder:text-slate-400 focus:border-emerald-500/50 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 sm:w-64'
                  />
                </div>
                <Button
                    label='Filter'
                    icon={<FilterIcons className='w-3 h-3' />}
                    hasIcon
                    onClick={handleOpenFilter}
                    variant='link'
                    className='!h-8 !px-2 !text-xs !text-slate-500 hover:!text-emerald-600'
                />
              </div>
            </div>

            { /* Skills Section */ }
            <div className='bg-white'>
              <div className='flex flex-col gap-2 px-2 py-2'>
                <div className='flex justify-between items-center'>
                  <h3 className='text-sm font-semibold text-gray-900'>
                    Skills Overview
                  </h3>
                  <button
                      onClick={toggleAllSkills}
                      className='rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-blue-700'
                  >
                    { showAllSkills ? 'Hide All Skills' : 'Show All Skills' }
                  </button>
                </div>
                <div className='grid grid-cols-1 gap-2 overflow-hidden lg:grid-cols-2'>
                  { /* Technical Skills */ }
                  <div className='flex min-w-0 items-start gap-2'>
                    <div className='pt-0.5'>
                      <h4 className='whitespace-nowrap text-xs font-semibold text-gray-800'>
                        Technical:
                      </h4>
                    </div>
                    <div className='flex gap-2 flex-wrap'>
                      { skills && skills.length > 0 ? (
                        skills.map((skill) => (
                          <TechnicalSkillButton
                              key={skill}
                              label={skill}
                              id={skill}
                              onClick={() => toggleSkillVisibility(skill)}
                              isActive={visibleSkills.has(skill)}
                          />
                        ))
                      ) : (
                        <NoDataInList
                            heading='No Technical Skills'
                            description='No technical skills available for this assessment'
                        />
                      ) }
                    </div>
                  </div>

                  { /* Behavioral Skills */ }
                  <div className='flex min-w-0 items-start gap-2'>
                    <div className='pt-0.5'>
                      <h4 className='whitespace-nowrap text-xs font-semibold text-gray-800'>
                        Behavioral:
                      </h4>
                    </div>
                    <div className='flex flex-wrap gap-2'>
                      { behaviorSkills && behaviorSkills.length > 0 ? (
                        Array.from(new Set(behaviorSkills)).map(
                          (behaviorSkill) => (
                            <BehavioralSkillButton
                                key={behaviorSkill}
                                label={behaviorSkill}
                                id={behaviorSkill}
                                onClick={() =>
                                  toggleSkillVisibility(behaviorSkill)
                                }
                                isActive={visibleSkills.has(behaviorSkill)}
                            />
                          ),
                        )
                      ) : (
                        <NoDataInList
                            heading='No Behavioral Skills'
                            description='No behavioral skills available for this assessment'
                        />
                      ) }
                    </div>
                  </div>
                </div>
              </div>
              { /* Action Buttons Row */ }
              <div className='flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 px-2 py-2'>
                <div className='flex items-center gap-2'>
                  { isAutoAssessment && (
                    <div className='flex items-center gap-2'>
                      { /* Selection Logic */ }
                      <div className='relative'>
                        <button
                            onClick={handleToggleSelectionDropdown}
                            className='flex h-8 items-center gap-2 rounded-lg border border-gray-200 bg-white px-2.5 text-xs font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
                        >
                          <input
                              type='checkbox'
                              className='form-checkbox h-4 w-4 text-blue-600 transition duration-150 ease-in-out'
                              checked={selectedItems.length > 0}
                              onChange={() => {}}
                          />
                          <span>Select</span>
                          <ChevronDownIcons
                              className={`h-4 w-4 transition-transform ${
                                selectionDropdownOpen ? 'rotate-180' : ''
                              }`}
                          />
                        </button>

                        { selectionDropdownOpen && (
                          <div className='absolute z-50 mt-2 w-48 rounded-lg bg-white shadow-xl border border-gray-200'>
                            <div className='py-1'>
                              <button
                                  onClick={() => handleSelectAllItems('page')}
                                  className='flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-blue-50'
                              >
                                Select Current Page
                              </button>
                              <button
                                  onClick={() => handleSelectAllItems('all')}
                                  className='flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-blue-50'
                              >
                                Select All Candidates
                              </button>
                            </div>
                          </div>
                        ) }
                      </div>

                      { /* Bulk Actions */ }
                      { selectedItems.length > 0 && (
                        <div className='relative'>
                          <button
                              onClick={handleToggleBulkActionDropdown}
                              className='flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
                          >
                            <span>Bulk Move ({ selectedItems.length })</span>
                            <ChevronDownIcons
                                className={`h-4 w-4 transition-transform ${
                                  bulkActionOpen ? 'rotate-180' : ''
                                }`}
                            />
                          </button>

                          { bulkActionOpen && (
                            <div className='absolute z-50 mt-2 w-64 rounded-lg bg-white shadow-xl border border-gray-200'>
                              <div className='py-1'>
                                { [
                                  'Waitlisted',
                                  'Shortlisted',
                                  'Unsuccessful',
                                ].map((option) => (
                                  <button
                                      key={option}
                                      onClick={() => handleBulkMove(option)}
                                      className='flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-blue-50'
                                  >
                                    <div className='w-2 h-2 rounded-full bg-blue-500 mr-3'></div>
                                    Move to { option }
                                  </button>
                                )) }
                              </div>
                            </div>
                          ) }
                        </div>
                      ) }
                    </div>
                  ) }
                </div>

                <div className='flex items-center gap-2'>
                  { isAutoAssessment && selectedItems.length > 0 && (
                    <Button
                        variant='secondary'
                        onClick={handleSendUnsuccessfulEmails}
                        label='Send Unsuccessful Email(s)'
                    />
                  ) }
                  <Button
                      variant='primary'
                      onClick={() => {
                        if (selectedRows.length > 0) {
                          toast.success('Selected candidates marked as checked', {
                            position: 'bottom-center',
                          });
                          setSelectedRows([]);
                        }
                      }}
                      disabled={selectedRows.length === 0}
                      label=' Mark As Checked'
                      className='!h-8 !px-4 !text-xs'
                  />
                  <Button
                      variant='primary'
                      onClick={() => exportCSV()}
                      label='Export Candidate CSV'
                      className='!h-8 !px-4 !text-xs'
                  />
                </div>
              </div>
            </div>
            { Object.keys(appliedFilters).length > 0 && (
              <AppliedFilters
                  appliedFilters={appliedFilters}
                  handleClearFilters={handleClearFilters}
                  handleRemoveFilter={handleRemoveFilter}
              />
            ) }
            <div className='w-full h-[calc(100dvh-320px)] min-h-[360px] min-w-0 sm:h-[calc(100vh-280px)]'>
              { (() => {
                const displayData =
                  transformUserResultsToApiFormat(serverUserResults);

                return (
                  <DataTable
                      hasCheckBox
                      isLoading={loading || !hasFetchedOnce}
                      columns={(() => {
                        const baseColumns = getColumns(
                          skills && skills.length > 0,
                          behaviorSkills && behaviorSkills.length > 0,
                        );

                        // Split visible skills into technical and behavioral
                        const visibleTechnicalSkills = new Set(
                          Array.from(visibleSkills).filter((s) =>
                            skills.includes(s),
                          ),
                        );
                        const visibleBehaviorSkills = new Set(
                          Array.from(visibleSkills).filter((s) =>
                            behaviorSkills.includes(s),
                          ),
                        );

                        const dynamicSkills = getDynamicSkillColumns(
                          visibleTechnicalSkills,
                        );
                        const dynamicBehaviors = getDynamicBehaviorColumns(
                          visibleBehaviorSkills,
                        );

                        const insertIndex = 4; // After Behaviour Score
                        return [
                          ...baseColumns.slice(0, insertIndex),
                          ...dynamicSkills,
                          ...dynamicBehaviors,
                          ...baseColumns.slice(insertIndex),
                        ];
                      })()}
                      data={displayData}
                      rowsPerPage={20} // Server page size
                      onPageChange={onPageChange}
                      currentPage={pageNumber}
                      totalPages={totalPages}
                      onSortChange={handleSortChange}
                      sortConfig={Object.entries(sort).map(
                        ([key, direction]) => ({
                          key,
                          direction,
                        }),
                      )}
                      totalRecords={serverTotalCount}
                      onClickRow={(assessmentResult) => {
                        if (onResultClick) {
                          onResultClick(assessmentResult);
                        } else {
                          history.push(
                            isAutoAssessment
                              ? `${autoAssessmentRoute}/${assessmentResult.assessmentId}/${assessmentResult.userId}/${assessmentResult.id}`
                              : `/view-assessment-result/${assessmentResult.assessmentId}/${assessmentResult.userId}/${assessmentResult.id}`,
                          );
                        }
                      }}
                      onSelectAll={() => {
                        if (
                          displayData.every((row) =>
                            selectedItems.includes(row.userId),
                          )
                        ) {
                          setSelectedItems(
                            selectedItems.filter(
                              (id) =>
                                !displayData.some((row) => row.userId === id),
                            ),
                          );
                        } else {
                          const newSelectedItems = new Set(selectedItems);
                          displayData.forEach((row) =>
                            newSelectedItems.add(row.userId),
                          );
                          setSelectedItems(Array.from(newSelectedItems));
                        }
                      }}
                      onSelectRow={(_index, row) => {
                        if (selectedItems.includes(row.userId)) {
                          setSelectedItems(
                            selectedItems.filter((id) => id !== row.userId),
                          );
                        } else {
                          setSelectedItems([...selectedItems, row.userId]);
                        }
                      }}
                      isRowSelected={(_index, row) =>
                        selectedItems.includes(row.userId)
                      }
                      isAllSelected={
                        displayData.length > 0 &&
                        displayData.every((row) =>
                          selectedItems.includes(row.userId),
                        )
                      }
                      isIndeterminate={
                        displayData.some((row) =>
                          selectedItems.includes(row.userId),
                        ) &&
                        !displayData.every((row) =>
                          selectedItems.includes(row.userId),
                        )
                      }
                  />
                );
              })() }
            </div>
          </div>
        </div>
      </div>
      <Drawer
          isOpen={filterOpen}
          onClose={handleCloseFilter}
          footerContent={
            <div className='flex justify-between items-center bg-slate-50/50 p-4 border-t border-slate-100 rounded-b-2xl'>
              <Button
                  label='Cancel'
                  variant='secondary'
                  className='!px-6 !py-2 !h-auto !text-[13px]'
                  onClick={handleCloseFilter}
              />
              <div className='flex gap-3 items-center'>
                <Button
                    label='Reset'
                    variant='link'
                    className='!text-slate-400 hover:!text-slate-600 !px-4 !py-2 !h-auto !text-[13px] !font-semibold'
                    onClick={handleClearFilters}
                />
                <Button
                    label='Apply Filters'
                    className='!px-8 !py-2 !h-auto !text-[13px] bg-emerald-600 hover:bg-emerald-700 !shadow-lg !shadow-emerald-500/20'
                    onClick={applyFilter}
                />
              </div>
            </div>
          }
          title='Filters'
          width='700px'
      >
        <FilterPreScreen
            questions={preScreenQuestions}
            setFilters={setFilters}
            filters={filters}
        />
      </Drawer>
    </>
  );
};
